import {
  ActionIcon,
  Button,
  Group,
  Loader,
  LoadingOverlay,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useFocusTrap } from '@mantine/hooks';
import {
  IconBadgeCc,
  IconKeyboard,
  IconLoader,
  IconMicrophone,
  IconMicrophoneOff,
  IconMoodHappy,
  IconPhone,
  IconRefresh,
  IconVolume,
  IconVolumeOff,
  IconX,
} from '@tabler/icons';
import cx from 'clsx';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { getMSTokenInfo, MSTokenInfo, RobotInfo } from '@mygpt/app/apis';
import { useChatStore, useQuestion, useVoiceRealtime } from '@mygpt/app/chat';
import {
  ChatContent,
  I_SignatureMessages,
  QuestionComplete,
  TextDot,
  VoicePreviewBubble,
} from '@mygpt/app/chat/components';
import ChatScroll from '@mygpt/app/chat/components/ChatScroll';
import { SvgSendStop } from '@mygpt/app/chat/components/icon';
import { getReferencesService, SearchQuestionItem } from '@mygpt/app/chat/services';
import { useDigitalHuman } from '@mygpt/app/digital-human';
import { isDescendant } from '@mygpt/app/utils';
import { PageInfo, ReferenceSign, ChatMode } from '@mygpt/types';

import { I_DigitalHuman, I_IconSetting } from '@/components/WidgetSetting/form-context';
import { SpeechText, useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import { SvgBadgeCcFilled, SvgRealtime, SvgSend } from '@/pages/ChatWidget/icon';
import { SessionListItem, SessionListParam } from '@/utils/interface';

import ChatbotAvatar from './ChatbotAvatar';
import classes from './chatView.module.css';
import { DigitalHuman } from './DigitalHuman';
import { DigitalHuman2D } from './DigitalHuman2D';

import mixpanel from 'mixpanel-browser';
import { MIXPANEL_TOKEN } from '@/constant';
import { FooterIcon, MsgWrap, TitleTag } from '@/pages/ChatWidget/components';
import { RealtimeIcon } from '@/pages/ChatWidget/components/RealtimeIcon';
import { SelectLanguage } from '@/components/SelectLanguage';
import { useGetLanguageInfo } from '@/hooks/useGetLanguageInfo';
import { getStorageValue } from '@mygpt/app/utils';

export interface I_configs {
  multiLanguageTitle?: { zh: string; en: string; ja: string; ko: string };
  signatureMessages?: I_SignatureMessages;
  welcomeMessages?: string[] | { zh: string[]; en: string[]; ja: string[]; ko: string[] };
  suggestedMessages?: string[] | { id: string; question: string }[];
  initMessages?: SessionListParam[];
  inputPlaceholder?: string;
  footerBrand?: string;
  answerSource?: {
    checked: boolean;
  };
  widgetColor?: {
    chatColor?: string;
    sendButtonColor?: string;
    userMessageColor?: string;
    botMessageColor?: string;
  };
  chatbotAvatar: I_IconSetting;
  digitalHuman: I_DigitalHuman;
}

interface Props {
  title: string;
  onClose: () => void;
  onRefresh: () => void;
  isFrozen: boolean;
  configs?: I_configs;
  sessionInfo: {
    aiId: string;
    sessionId: string;
    pageInfo: PageInfo;
  };
  robotInfo?: RobotInfo;
}

export default (props: Props) => {
  const { t } = useTranslation();
  const { configs, isFrozen, onClose, onRefresh: refreshId, robotInfo, sessionInfo, title } = props;
  const focusTrapRef = useFocusTrap();
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef(null);

  const isDev = ['dev', 'localhost'].some((key) => window.location.host.includes(key));

  const { setTtsMuted, ttsMuted } = useChatStore();
  const { voiceLanguage } = useGetLanguageInfo();

  const [inputFocus, setInputFocus] = useState(true);

  useEffect(() => {
    inputRef.current && focusTrapRef(inputRef.current);
  }, [focusTrapRef, inputRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedElement = event.target as HTMLElement;
      const formElement = formRef.current;
      if (formElement && !isDescendant(clickedElement, formElement)) {
        setInputFocus(false);
      } else {
        setInputFocus(true);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const form = useForm({
    initialValues: {
      value: '',
    },
  });

  const {
    answerSource: configsAnswerSource,
    chatbotAvatar,
    footerBrand,
    initMessages,
    inputPlaceholder,
    signatureMessages,
    suggestedMessages,
    welcomeMessages,
    widgetColor,
    digitalHuman,
  } = configs ?? {};
  const { botMessageColor, chatColor, sendButtonColor, userMessageColor } = widgetColor ?? {};

  const messageId = uuid();
  const [historySessionList, setHistorySessionList] = useState<SessionListItem>({ messages: [] });
  const { clearSessionList, fetchQuestion, loading, sessionList, stopAnswer, updateSessionList } = useQuestion({
    aiId: sessionInfo.aiId || '',
    sessionId: sessionInfo.sessionId,
    messageId,
    pageInfo: sessionInfo.pageInfo,
  });
  const { messages, pending } = sessionList;
  console.log('messages', messages, 'pending', pending);

  const chatMessages = useMemo(() => {
    const historySessionListMessage = historySessionList ? historySessionList.messages : [];
    return [
      ...(initMessages || []),
      ...historySessionListMessage,
      ...messages,
      ...(pending
        ? [
            {
              isAnswer: true,
              text: pending,
              rating: 0,
              messageId,
            },
          ]
        : []),
    ];
  }, [sessionList, historySessionList, initMessages]);

  // useEffect(() => {
  //   // 当对话内容为空时，更新初始消息
  //   if (!messages.length) {
  //     setHistorySessionList({ messages: initMessages || [] });
  //   }
  // }, [initMessages]);

  const {
    closeSessionData,
    canUseDigitalHuman,
    closeSession,
    digitalHumanReady,
    init: digitalHumanInit,
    playStreamAddr,
    sendTextMessage,
  } = useDigitalHuman();

  const formatTextForSpeech = (markdownText: string): string => {
    return markdownText
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // 移除链接，只保留链接文本
      .replace(/[*_~`]/g, '') // 移除用于格式化的特殊字符
      .replace(/#+\s/g, '') // 移除标题标记
      .replace(/\n/g, ' ') // 将换行符替换为空格
      .trim(); // 移除首尾空白字符
  };

  useEffect(() => {
    if (!loading && digitalHumanReady) {
      const lastMessage = chatMessages[chatMessages.length - 1];
      if (lastMessage) {
        const formattedText = formatTextForSpeech(lastMessage.text);
        sendTextMessage(formattedText);
      }
    }
  }, [loading, digitalHumanReady, chatMessages]);

  const answerSource = useMemo(() => {
    if (!configsAnswerSource) return true;
    return configsAnswerSource?.checked;
  }, [configsAnswerSource]);

  const isDigitalHumanRobot = robotInfo?.robot_type === 'digital_human';

  const is2dDigitalPersonRobot = robotInfo?.configs.enable_2d_digital_person;

  const isDigitalHuman = useMemo(() => {
    return robotInfo?.robot_type === 'digital_human' || robotInfo?.configs.enable_2d_digital_person;
  }, [robotInfo]);

  const enableTTS = useMemo(() => {
    return robotInfo?.configs.enable_tts || is2dDigitalPersonRobot;
  }, [robotInfo?.configs.enable_tts, is2dDigitalPersonRobot]);

  const onRefresh = () => {
    refreshId();
    setHistorySessionList({ messages: [] });
    clearSessionList();
  };

  const isLastMessage = (messageId: string) => {
    if (historySessionList?.messages.length) {
      return historySessionList.messages.at(-1)?.messageId === messageId;
    } else if (messages.length) {
      return messages.at(-1)?.messageId === messageId;
    } else {
      return false;
    }
  };

  const submit = async (values: { value: string; faqId?: string }, event?: FormEvent<HTMLFormElement>) => {
    event && event.preventDefault();
    if (loading) return;
    const { faqId, value } = values;
    submitCompletion(value, faqId);
  };

  const submitCompletion = async (value: string, faqID?: string) => {
    form.reset();
    fetchQuestion(value, {
      faqID,
    });
  };

  const handleMessages = ({ messageId, rating }: { rating: number; messageId: string }) => {
    if (!historySessionList) return;
    const hisroryList = historySessionList.messages;
    const historyIndex = hisroryList.findIndex((item: SessionListParam) => item.messageId === messageId);
    hisroryList[historyIndex].rating = rating;
    setHistorySessionList({ messages: hisroryList });
  };

  const getReference = (ai_id: string, message_id: string) => {
    return new Promise<ReferenceSign[]>((resolve) => {
      getReferencesService(ai_id, message_id).then((items) => {
        setHistorySessionList((list) => {
          const messages = list.messages.map((item) => {
            if (item.messageId === message_id) {
              item.reference_indexes = items.data;
            }
            return item;
          });
          return { ...list, messages };
        });
        resolve(items.data);
      });
    });
  };

  const saveChat = () => {
    // 有一问一答的时候才记录
    if (messages.length && messages.at(-1)?.isAnswer) {
      const chatList = [...historySessionList.messages, ...messages.slice(-2)];
      setHistorySessionList({ messages: chatList });
      updateSessionList({ messages: [] });
    }
  };

  useEffect(() => {
    // 如果不是数字人bot，表示功能不启用，不调接口
    if (!isDigitalHuman || !voiceLanguage || msTokenError) return;
    // 如果正在录音，则先结束录音状态再重新开始
    const recordingStatus = isRecording;
    if (isRecording) {
      stop();
      clearAllTimers();
      clearVoiceText();
    }
    if (msTokenInfo.STTToken) {
      startInit(msTokenInfo.STTToken, [voiceLanguage]);
      recordingStatus && startRecording();
    } else {
      getMSTokenInfo()
        .then((info) => {
          setMSTokenInfo(info);
          startInit(info.STTToken, [voiceLanguage]);
        })
        .catch((error) => {
          setMSTokenError(true);
          console.error('Error getting MSTokenInfo:', error);
        });
    }
    setRealtimeLanguage(voiceLanguage);
  }, [isDigitalHuman, voiceLanguage]);

  // 聊天记录
  useEffect(() => {
    if (!messages.length) return;
    saveChat();
  }, [messages]);

  // 收到嵌入网页发送消息的指令
  useEffect(() => {
    const getParentMessage = (e) => {
      const { question, type } = e.data;
      if (type === 'send' && question) {
        submit({ value: question });
      }
    };
    window.addEventListener('message', getParentMessage);

    return () => {
      window.removeEventListener('message', getParentMessage);
    };
  }, [loading, messageId]);

  // 页面销毁前的处理
  useEffect(() => {
    const startTime = new Date();
    const handleBeforeUnload = () => {
      try {
        // 关闭数字人session
        if (isDigitalHumanRobot && closeSessionData.url) {
          const closeSessionUrl = closeSessionData.url;
          const requestData = closeSessionData.data;

          if (navigator.sendBeacon) {
            navigator.sendBeacon(closeSessionUrl, JSON.stringify(requestData));
          } else {
            closeSession();
          }
        }

        // 记录停留时间
        if (startTime) {
          const duration = (new Date().getTime() - startTime.getTime()) / 1000; // 停留时间，单位为秒
          const eventData = {
            event: 'page_duration',
            properties: {
              duration,
              token: MIXPANEL_TOKEN,
              distinct_id: mixpanel.get_distinct_id(),
              url: mixpanel.get_property('url'),
              'Current Url': window.location.href,
              'User Agent': navigator.userAgent,
            },
          };

          const payload = {
            data: btoa(JSON.stringify(eventData)),
          };

          const mixpanelUrl = 'https://api.mixpanel.com/track/';
          const mixpanelParams = new URLSearchParams(payload).toString();

          if (navigator.sendBeacon) {
            navigator.sendBeacon(mixpanelUrl, mixpanelParams);
          } else {
            mixpanel.track('page_duration', { duration });
          }
        }
      } catch (error) {
        console.log('handleBeforeUnload error', error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [closeSessionData, isDigitalHumanRobot]);

  // 以下是音频部分
  const [isVoiceMode, setIsVoiceMode] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [sendLoading, setSendLoading] = useState<boolean>(false);

  const [recognizeText, setRecognizeText] = useState<string>('');
  const [temporaryText, setTemporaryText] = useState<string>('');

  const voiceText = useMemo(() => {
    return recognizeText + temporaryText;
  }, [recognizeText, temporaryText]);

  const [msTokenInfo, setMSTokenInfo] = useState<MSTokenInfo>({} as MSTokenInfo);
  const [msTokenError, setMSTokenError] = useState<boolean>(false);

  const onRecognizing = (data: SpeechText) => {};

  const onRecognized = (data: SpeechText) => {};

  const { speechText, startInit, startSpeak, stop } = useSpeechRecognition(
    ['ja-JP', 'en-US', 'zh-CN', 'ko-KR'],
    msTokenInfo.STTToken,
    onRecognized,
    onRecognizing
  );

  useEffect(() => {
    if (speechText.recognizing) {
      setTemporaryText(speechText.text);
    } else {
      setRecognizeText(recognizeText + speechText.text);
      setTemporaryText('');
    }
  }, [speechText]);

  // 修改状态声明
  const {
    realtimeStatus,
    realtimeMessages,
    join,
    leave,
    joinLoading,
    isMicrophoneOn,
    toggleMicrophone,
    setLanguage: setRealtimeLanguage,
  } = useVoiceRealtime({
    botId: sessionInfo.aiId,
    baseUrl: import.meta.env.VITE_REALTIME_URL,
  });
  const [chatMode, setChatMode] = useState<ChatMode>('text');

  // 修改切换语音模式的函数
  const toggleVoiceMode = () => {
    if (chatMode === 'voice') {
      setChatMode('text');
      handleCancel();
    } else {
      setChatMode('voice');
      startRecording();
    }
  };

  // 添加切换实时模式的函数
  const toggleRealtime = () => {
    if (chatMode === 'realtime') {
      setChatMode('text');
      leave();
    } else {
      setChatMode('realtime');
      try {
        join();
      } catch (error) {
        setChatMode('text');
      }
    }
  };

  // 添加新的状态
  const [shouldBlink, setShouldBlink] = useState<boolean>(false); // 控制按钮闪烁
  const recordingTimerRef = useRef<NodeJS.Timeout>(); // 30s 总计时器
  const blinkTimerRef = useRef<NodeJS.Timeout>(); // 20s 闪烁计时器

  // 清理所有计时器
  const clearAllTimers = () => {
    if (recordingTimerRef.current) clearTimeout(recordingTimerRef.current);
    if (blinkTimerRef.current) clearTimeout(blinkTimerRef.current);
  };

  // 重置到初始状态
  const resetToInitial = () => {
    clearAllTimers();
    handleCancel();
    setShouldBlink(false);
  };

  // 清理语音文本
  const clearVoiceText = () => {
    setTemporaryText('');
    setRecognizeText('');
  };

  // 开始录音时设置计时器
  const startRecording = () => {
    setIsRecording(true);
    clearVoiceText();
    startSpeak();

    // 30s 后自动重置
    recordingTimerRef.current = setTimeout(() => {
      resetToInitial();
    }, 30000);

    // 20s 后按钮闪烁
    blinkTimerRef.current = setTimeout(() => {
      setShouldBlink(true);
    }, 20000);
  };

  // 结束录音时清理计时器
  const handleEnd = () => {
    clearAllTimers();
    setIsRecording(false);
    setSendLoading(true);
    setShouldBlink(false);

    voiceText && submitCompletion(voiceText);
    stop();
    clearVoiceText();

    setTimeout(() => {
      setSendLoading(false);
    }, 1000);
  };

  const handleCancel = () => {
    clearAllTimers();
    clearVoiceText();
    setIsRecording(false);
    setShouldBlink(false);
    stop();
  };

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, []);

  // 添加当前播放消息的状态
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);
  // 整段朗读切换时需要打断正在读的音频，但是用isPlaying判断不准确，所以新增一个变量
  const [fullAnswerPlay, setFullAnswerPlay] = useState<boolean>(false);

  // 处理音频播放控制
  const handleAudioControl = (messageId: string | null, fullAnswerPlay?: boolean) => {
    if (playingMessageId && playingMessageId !== messageId) {
      setPlayingMessageId(null);
      // console.log('检测到当前播放消息切换');
      fullAnswerPlay && setFullAnswerPlay(true);
      setTimeout(() => {
        // console.log('恢复整段播放的标记');
        setFullAnswerPlay(false);
      }, 2000);
    }
    // 延迟设置播放消息状态，防止整段朗读切换时打断正在读的音频
    setTimeout(() => {
      // console.log('设置正确的播放消息状态');
      setPlayingMessageId(messageId);
    }, 1000);
  };

  const [temporaryPause, setTemporaryPause] = useState(false);

  // 监听 loading 的变化
  useEffect(() => {
    if (loading) {
      setTemporaryPause(true);
      // 500ms 后自动重置
      const timer = setTimeout(() => {
        setTemporaryPause(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const ttsPause = useMemo(() => {
    return temporaryPause || isRecording || ttsMuted || fullAnswerPlay;
  }, [temporaryPause, isRecording, ttsMuted, fullAnswerPlay]);

  useEffect(() => {
    ttsPause && setPlayingMessageId(null);
  }, [ttsPause]);

  // 关闭widget窗口时打断tts
  const onCloseWidget = () => {
    onClose();
    if (!ttsMuted) {
      setTtsMuted(true);
      setTimeout(() => {
        setTtsMuted(false);
      }, 1000);
    }
    // 如果正在实时聊天，则关闭麦克风
    if (chatMode === 'realtime' && isMicrophoneOn) {
      toggleMicrophone();
    }
  };

  const [showRealtimeMsg, setShowRealtimeMsg] = useState<boolean>(true);

  // 根据数字人设置修改背景色
  useEffect(() => {
    isDigitalHuman &&
      (document.body.style.backgroundColor = digitalHuman?.backgroundColor.checked
        ? digitalHuman?.backgroundColor.value || '#c5c9ee'
        : 'transparent');
  }, [digitalHuman]);

  return (
    <div className={`h-full flex flex-col backdrop-blur-sm`}>
      {/* widget header */}
      {isDigitalHuman ? (
        <Group className="flex-none py-5 px-4 overflow-hidden" justify="space-between" wrap="nowrap" gap={8}>
          <Group wrap="nowrap" gap={5} className="flex-1 min-w-0">
            <TitleTag className="min-w-0 flex-shrink overflow-hidden text-ellipsis whitespace-nowrap">{title}</TitleTag>
            <TitleTag className="flex-none flex items-center">
              <ActionIcon variant="transparent" size="xs" color="white" onClick={() => setTtsMuted(!ttsMuted)}>
                {ttsMuted ? <IconVolumeOff /> : <IconVolume />}
              </ActionIcon>
            </TitleTag>
            <TitleTag className="flex-none flex items-center">
              <ActionIcon variant="transparent" size="xs" color="white" onClick={onRefresh}>
                <IconRefresh />
              </ActionIcon>
            </TitleTag>
            <TitleTag className="flex-none flex items-center">
              <SelectLanguage isDigitalHuman={isDigitalHuman} />
            </TitleTag>
          </Group>
          <ActionIcon variant="transparent" size="sm" color="gray" onClick={onCloseWidget} className="flex-none">
            <IconX />
          </ActionIcon>
        </Group>
      ) : (
        <section
          className={`${classes.header} flex-none py-5 pl-5 pr-2 overflow-hidden`}
          style={{ background: chatColor ? chatColor : 'linear-gradient(90.07deg, #AF71FF 4.16%, #7C68FB 134.24%)' }}
        >
          <Group justify="space-between" wrap="nowrap">
            <Title
              style={{ wordBreak: 'break-word' }}
              order={5}
              className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
            >
              {title}
            </Title>
            <Group wrap="nowrap" gap={2} className="flex-none">
              <div className="flex-none mr-1">
                <SelectLanguage isWidget />
              </div>
              {enableTTS && (
                <Tooltip label={ttsMuted ? t('completions.tts.open') : t('completions.tts.close')} withArrow>
                  <ActionIcon variant="subtle" onClick={() => setTtsMuted(!ttsMuted)} className={classes.headerIcon}>
                    {ttsMuted ? <IconVolumeOff size={18} /> : <IconVolume size={18} />}
                  </ActionIcon>
                </Tooltip>
              )}
              <Tooltip label={t('completions.reset_chat')}>
                <ActionIcon variant="subtle" className={classes.headerIcon} onClick={onRefresh}>
                  <IconRefresh size={18} />
                </ActionIcon>
              </Tooltip>
              <ActionIcon variant="subtle" className={classes.headerIcon} onClick={onCloseWidget}>
                <IconX size={18} />
              </ActionIcon>
            </Group>
          </Group>
        </section>
      )}

      {/* widget tip */}
      {isFrozen && (
        <section className="bg-yellow-100 p-2">
          <Text fz="xs">{t('dataset.frozen_text')}</Text>
        </section>
      )}

      {/* widget body */}
      {((isDigitalHumanRobot && digitalHumanReady) || is2dDigitalPersonRobot) && (
        <div className="h-[80%] bg-transparent" />
      )}
      <ChatScroll
        containerScroll={(e) => {
          const container = e;
          const containerRect = container.getBoundingClientRect();
          const messages = container.querySelectorAll('.message-wrap');

          messages.forEach((message) => {
            const elementRect = message.getBoundingClientRect();
            // 使用元素底部位置来计算透明度
            const elementBottom = elementRect.bottom;
            const distance = elementBottom - containerRect.top;
            const maxDistance = 100; // 渐变效果的距离范围
            const opacity = Math.min(1, Math.max(0, distance / maxDistance));
            (message as HTMLElement).style.opacity = opacity.toString();
          });
        }}
      >
        <section className={`${classes.answerWrap} flex-grow overflow-y-auto relative`}>
          {chatMode === 'realtime' ? (
            showRealtimeMsg ? (
              realtimeMessages.map((item, index) => {
                return (
                  <div
                    key={index}
                    className={cx(
                      classes.sessionItem,
                      item.isAnswer ? classes.answerText : classes.questionText,
                      'flex gap-3 break-all message-wrap'
                    )}
                  >
                    <div className="min-w-0">
                      <MsgWrap>
                        <span>{item.text}</span>
                      </MsgWrap>
                    </div>
                  </div>
                );
              })
            ) : (
              <></>
            )
          ) : (
            chatMessages.map((item, index) => {
              const questionMessageColor = userMessageColor ? userMessageColor : '#7C68FB';
              const answerMessageColor = botMessageColor ? botMessageColor : '#F9FAFB';
              return (
                <div
                  key={index}
                  className={cx(
                    classes.sessionItem,
                    item.isAnswer ? classes.answerText : classes.questionText,
                    'flex gap-3 break-all'
                  )}
                >
                  {item.isAnswer && !isDigitalHuman && (
                    <span>
                      <ChatbotAvatar avatar={chatbotAvatar} />
                    </span>
                  )}
                  <div className="min-w-0">
                    {isDigitalHuman ? (
                      <MsgWrap className="message-wrap">
                        <ChatContent
                          id={sessionInfo?.aiId || ''}
                          item={item}
                          index={index}
                          answerSource={answerSource}
                          pending={pending}
                          chatMessages={chatMessages}
                          sessionId={sessionInfo?.sessionId || ''}
                          signatureMessages={signatureMessages}
                          fetchQuestion={fetchQuestion}
                          isLastMessage={isLastMessage}
                          regenerate={fetchQuestion}
                          handleMessages={handleMessages}
                          getReference={getReference}
                          pageSource="widget"
                          robotInfo={robotInfo}
                          isPlaying={playingMessageId === item.messageId}
                          ttsPause={ttsPause}
                          ttsLanguage={voiceLanguage}
                          onAudioControl={handleAudioControl}
                          isDigitalHuman={isDigitalHuman}
                        />
                      </MsgWrap>
                    ) : (
                      <div
                        className={cx(classes.chatContent, !item.isAnswer ? classes.questionWrap : '')}
                        style={{ backgroundColor: !item.isAnswer ? questionMessageColor : answerMessageColor }}
                      >
                        <ChatContent
                          id={sessionInfo?.aiId || ''}
                          item={item}
                          index={index}
                          answerSource={answerSource}
                          pending={pending}
                          chatMessages={chatMessages}
                          sessionId={sessionInfo?.sessionId || ''}
                          signatureMessages={signatureMessages}
                          fetchQuestion={fetchQuestion}
                          isLastMessage={isLastMessage}
                          regenerate={fetchQuestion}
                          handleMessages={handleMessages}
                          getReference={getReference}
                          pageSource="widget"
                          robotInfo={robotInfo}
                          isPlaying={playingMessageId === item.messageId}
                          ttsPause={ttsPause}
                          ttsLanguage={voiceLanguage}
                          onAudioControl={handleAudioControl}
                        />
                      </div>
                    )}
                    {item.isAnswer &&
                      (initMessages || []).length - 1 === index &&
                      suggestedMessages?.some((item) => !!item) && (
                        <section className="flex-none py-2">
                          {welcomeMessages &&
                            (Array.isArray(welcomeMessages)
                              ? welcomeMessages.some((item) => !!item)
                              : Object.values(welcomeMessages).some((arr) => arr.some((item) => !!item))) && (
                              <p className="text-xs text-gray-400 text-center mt-3 mb-5 message-wrap">
                                {t('widget.setting.chatbot.suggested_greeting_message')}
                              </p>
                            )}
                          <Stack gap={'sm'}>
                            {suggestedMessages.map((item, index) => (
                              <div key={index}>
                                {isDigitalHuman ? (
                                  <MsgWrap className="message-wrap">
                                    <p
                                      className={` cursor-pointer`}
                                      onClick={() => {
                                        typeof item === 'string'
                                          ? submit({ value: item })
                                          : submit({ value: item.question, faqId: item.id });
                                      }}
                                    >
                                      {typeof item === 'string' ? item : item.question}
                                    </p>
                                  </MsgWrap>
                                ) : (
                                  <p
                                    className={`inline-block rounded-md p-2 text-sm cursor-pointer`}
                                    style={{ backgroundColor: answerMessageColor }}
                                    onClick={() => {
                                      typeof item === 'string'
                                        ? submit({ value: item })
                                        : submit({ value: item.question, faqId: item.id });
                                    }}
                                  >
                                    {typeof item === 'string' ? item : item.question}
                                  </p>
                                )}
                              </div>
                            ))}
                          </Stack>
                        </section>
                      )}
                  </div>
                </div>
              );
            })
          )}
          {loading &&
            !pending &&
            chatMode !== 'realtime' &&
            (isDigitalHuman ? (
              <div className={`${classes.sessionItem} flex items-start gap-3 break-all message-wrap`}>
                <MsgWrap>
                  <div className="whitespace-pre-wrap">
                    <TextDot />
                  </div>
                </MsgWrap>
              </div>
            ) : (
              <div className={`${classes.sessionItem} ${classes.answerText} flex items-start gap-3 break-all`}>
                <span>
                  <ChatbotAvatar avatar={chatbotAvatar} />
                </span>
                <div>
                  <div className="whitespace-pre-wrap">
                    {/* {t('completions.thinking')} */}
                    <TextDot />
                  </div>
                </div>
              </div>
            ))}
        </section>
      </ChatScroll>
      {isDigitalHumanRobot && !is2dDigitalPersonRobot && (
        <>
          <LoadingOverlay
            visible={!digitalHumanReady && canUseDigitalHuman}
            zIndex={1000}
            overlayProps={{ radius: 'sm', blur: 2 }}
          />
          <div className="fixed bottom-0 left-0 right-0 top-0 w-full h-full bg-gray-100 z-[-1]">
            <DigitalHuman
              init={digitalHumanInit}
              digitalHumanReady={digitalHumanReady}
              playStreamAddr={playStreamAddr}
              canUseDigitalHuman={canUseDigitalHuman}
            />
          </div>
        </>
      )}
      {is2dDigitalPersonRobot && (
        <div className="fixed bottom-0 left-0 right-0 top-0 w-full h-full z-[-1]">
          <DigitalHuman2D avatarType={digitalHuman?.image} />
        </div>
      )}

      <section className={`${classes.inputWrap} flex-none px-5 ${isDigitalHuman && 'border-none'}`}>
        {/* widget input */}
        <form onSubmit={form.onSubmit(submit)} className="relative" ref={formRef}>
          <QuestionComplete
            aiId={sessionInfo.aiId}
            question={form.values.value}
            handleQuestionClick={({ id, question }: SearchQuestionItem) => {
              submitCompletion(question, id);
            }}
            inputFocus={inputFocus}
            submitLoading={loading}
          />
          <div className={`${classes.input} flex items-center`}>
            {isDigitalHuman && chatMode !== 'realtime' && !msTokenError && (
              <FooterIcon className="mr-2">
                <ActionIcon
                  className={cx('ease-in duration-300')}
                  size="lg"
                  type="button"
                  radius="xl"
                  variant="subtle"
                  onClick={toggleVoiceMode}
                >
                  {chatMode === 'voice' ? <IconKeyboard size={16} /> : <IconMicrophone size={16} />}
                </ActionIcon>
              </FooterIcon>
            )}

            {chatMode === 'text' ? (
              <TextInput
                autoComplete="off"
                ref={inputRef}
                size="md"
                radius="xl"
                className={`flex-grow ${isDigitalHuman && ' '}`}
                placeholder={inputPlaceholder ? inputPlaceholder : t('completions.input.placeholder')}
                rightSectionPointerEvents="all"
                rightSectionWidth={60}
                rightSection={
                  <ActionIcon
                    style={{ color: sendButtonColor ? sendButtonColor : '' }}
                    type="submit"
                    radius="xl"
                    size="lg"
                    variant="subtle"
                  >
                    {loading ? <SvgSendStop onClick={stopAnswer} /> : <SvgSend />}
                  </ActionIcon>
                }
                {...form.getInputProps('value')}
              />
            ) : chatMode === 'voice' ? (
              <Group className="relative flex-grow" align="center">
                <VoicePreviewBubble
                  text={voiceText}
                  isRecording={isRecording}
                  isSending={isVoiceMode && !isRecording && sendLoading}
                  clear={handleCancel}
                />
                <Button
                  size="md"
                  className={cx(
                    `flex-grow py-2 px-4 rounded-full relative select-none touch-none transition-all text-sm`,
                    shouldBlink && 'animate-pulse'
                  )}
                  onClick={!isRecording ? startRecording : handleEnd}
                  loading={sendLoading}
                  disabled={loading || (isRecording && !voiceText)}
                  leftSection={
                    isRecording ? (
                      voiceText ? (
                        <span className={`${voiceText ? 'text-white' : 'text-gray-300'} scale-75`}>
                          <SvgSend />
                        </span>
                      ) : (
                        <IconMoodHappy size={16} />
                      )
                    ) : (
                      <IconMicrophone size={16} />
                    )
                  }
                  styles={(theme) => {
                    return {
                      root: {
                        background: isDigitalHuman
                          ? loading || (isRecording && !voiceText)
                            ? 'rgba(255, 255, 255, 0.5)'
                            : 'linear-gradient(338.86deg, rgba(110, 125, 255, 0.7) 13.57%, rgba(161, 139, 255, 0.7) 91.29%)'
                          : '',
                        color: isDigitalHuman ? (isRecording && !voiceText ? 'gray' : 'white') : '',
                        backdropFilter: 'blur(6px)',
                      },
                    };
                  }}
                >
                  <span className="font-bold">
                    {isRecording ? (voiceText ? t('widget.send') : t('widget.speak')) : t('widget.start_recording')}
                  </span>
                </Button>
                {loading && (
                  <ActionIcon radius="xl" variant="transparent" onClick={stopAnswer} className="ml-2">
                    <SvgSendStop />
                  </ActionIcon>
                )}
              </Group>
            ) : (
              <Group justify="center" gap={20} className="flex-grow mb-2">
                {joinLoading ? (
                  <RealtimeIcon>
                    <IconLoader size={20} className="animate-spin" />
                  </RealtimeIcon>
                ) : (
                  <Stack>
                    <Group justify="center" h={25}>
                      {realtimeStatus === 'speaking' && <Loader type="dots" size="sm" color="white" />}
                      {realtimeStatus === 'listening' && <Loader type="bars" size="sm" color="white" />}
                    </Group>
                    <Group gap={50}>
                      <RealtimeIcon>
                        <ActionIcon color="white" variant="transparent" onClick={toggleMicrophone}>
                          {isMicrophoneOn ? <IconMicrophone size={20} /> : <IconMicrophoneOff size={20} />}
                        </ActionIcon>
                      </RealtimeIcon>
                      <RealtimeIcon backgroundColor="rgba(255, 0, 0, 0.5)" size={50}>
                        <ActionIcon
                          radius="xl"
                          size="xl"
                          color="white"
                          variant="transparent"
                          onClick={() => {
                            setChatMode('voice');
                            leave();
                          }}
                        >
                          <IconPhone className="rotate-[135deg]" />
                        </ActionIcon>
                      </RealtimeIcon>
                      <RealtimeIcon>
                        <ActionIcon
                          color="white"
                          variant="transparent"
                          onClick={() => {
                            setShowRealtimeMsg(!showRealtimeMsg);
                          }}
                        >
                          {showRealtimeMsg ? <SvgBadgeCcFilled /> : <IconBadgeCc />}
                        </ActionIcon>
                      </RealtimeIcon>
                    </Group>
                  </Stack>
                )}
              </Group>
            )}

            {/* 暂时只在测试环境显示实时对话 */}
            {isDev && isDigitalHuman && chatMode !== 'realtime' && (
              <ActionIcon radius="xl" size="lg" variant="subtle" className="ml-2" onClick={toggleRealtime}>
                <SvgRealtime />
              </ActionIcon>
            )}
          </div>
        </form>
        {/* widget footer */}
        <section className="text-gray-400 min-h-[18px]">
          <Text ta="center" size="xs" mt={5} mb={5}>
            {footerBrand ? (
              footerBrand
            ) : (
              <>
                Powered by{' '}
                <a href="https://gbase.ai/" target="_blank" rel="noreferrer" className="text-violet-500">
                  GBase
                </a>
              </>
            )}
          </Text>
        </section>
      </section>
    </div>
  );
};
